import { useCallback } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { openDownloadFallbackTab } from "../utils/downloadFile";

/** Blob URLs stay valid this long, so the PDF viewer in the new tab can finish loading (and reload once). */
const OBJECT_URL_LIFETIME_MS = 5 * 60 * 1000;

/** Fetches a file that needs the user's JWT; throws on a non-OK response. */
const fetchAuthenticatedBlob = async (keycloak: import("keycloak-js").KeycloakInstance, url: string): Promise<Blob> => {
    // A file fetch can come long after the last GraphQL call — refresh a token that is about to expire.
    await keycloak.updateToken(30).catch(() => undefined);
    const response = await fetch(url, { headers: keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {} });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
};

/**
 * Opens a file that the backend only serves with the user's JWT in the
 * `Authorization` header (e.g. `/file/auth/…`) in a new tab. A plain link
 * cannot send that header — the token lives in memory, not in a cookie — so
 * the file is fetched with the token and shown from a blob URL.
 *
 * The tab is opened synchronously in the click handler (before the fetch),
 * otherwise the popup blocker eats it.
 */
export const useAuthenticatedFileOpen = () => {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();

    return useCallback(async (url: string, title: string) => {
        const tab = openDownloadFallbackTab(title);
        if (!tab) {
            toast.error(t("Please allow pop-ups for this site to open the document."));
            return;
        }
        try {
            const objectUrl = URL.createObjectURL(await fetchAuthenticatedBlob(keycloak, url));
            if (tab.closed) {
                URL.revokeObjectURL(objectUrl);
                return;
            }
            tab.location.replace(objectUrl);
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS);
        } catch {
            if (!tab.closed) tab.close();
            toast.error(t("The document could not be loaded."));
        }
    }, [keycloak, t]);
};

/**
 * Saves a file that needs the user's JWT (see useAuthenticatedFileOpen) under
 * `filename` — for documents a browser tab can't show, e.g. a .docx template.
 * No helper tab: a download needs no popup permission.
 */
export const useAuthenticatedFileDownload = () => {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();

    return useCallback(async (url: string, filename: string) => {
        try {
            const objectUrl = URL.createObjectURL(await fetchAuthenticatedBlob(keycloak, url));
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch {
            toast.error(t("The document could not be loaded."));
        }
    }, [keycloak, t]);
};
